import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { API_APPS_VERSIONS_URL } from '@/utils';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { AppsVersionsApi, AppsVersionsFilter, UpdateAppVersionSchema, UpdateAppVersionWithApkSchema, APPS_VERSIONS_FIELDS } from '../models';

const appsVersionsService = createApi({
  reducerPath: 'appsVersionsService',
  baseQuery: createBaseQueryWithReauth(API_APPS_VERSIONS_URL),
  tagTypes: ['AppsVersions'],
  endpoints: (builder) => ({
    getAppsVersions: builder.query<
      PaginatedResponse<AppsVersionsApi>,
      {
        paginated: PaginatedRequest;
        filter: AppsVersionsFilter;
      }
    >({
      query: ({ paginated, filter }) => ({
        url: '',
        params: {
          ...(filter.platform && { platform: filter.platform }),
          ...(filter.appName && { appName: filter.appName }),
          ...(filter.versionCode && { versionCode: filter.versionCode }),
          ...(filter.versionName && { versionName: filter.versionName }),
          ...(filter.minSupportedVersionCode && { minSupportedVersionCode: filter.minSupportedVersionCode }),
          ...(filter.enabled !== undefined && { enabled: filter.enabled }),
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      providesTags: ['AppsVersions'],
    }),
    uploadVersion: builder.mutation<AppsVersionsApi, FormData>({
      query: (data) => ({
        url: '/upload',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AppsVersions'],
    }),
    getAppVersionById: builder.query<AppsVersionsApi, string>({
      query: (id) => ({
        url: id,
      }),
      providesTags: (_result, _error, id) => [{ type: 'AppsVersions', id }],
    }),
    deleteAppVersion: builder.mutation<void, string>({
      query: (id) => ({
        url: id,
        method: 'DELETE',
      }),
      invalidatesTags: ['AppsVersions'],
    }),
    updateAppVersionWithoutApk: builder.mutation<
      AppsVersionsApi,
      { id: string; data: UpdateAppVersionSchema }
    >({
      query: ({ id, data }) => ({
        url: id,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AppsVersions', id },
        { type: 'AppsVersions' },
      ],
    }),
    updateAppVersionWithApk: builder.mutation<
      AppsVersionsApi,
      { id: string; data: UpdateAppVersionWithApkSchema }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        if (data.apk) {
          formData.append(APPS_VERSIONS_FIELDS.apk, data.apk);
        }
        formData.append(APPS_VERSIONS_FIELDS.versionName, data.versionName);
        formData.append(APPS_VERSIONS_FIELDS.minSupportedVersionCode, data.minSupportedVersionCode);
        formData.append(APPS_VERSIONS_FIELDS.forceUpdate, data.forceUpdate.toString());
        formData.append(APPS_VERSIONS_FIELDS.enabled, data.enabled.toString());
        return {
          url: `${id}/upload`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AppsVersions', id },
        { type: 'AppsVersions' },
      ],
    }),
  }),
});

export default appsVersionsService;
export const {
  useGetAppsVersionsQuery,
  useUploadVersionMutation,
  useGetAppVersionByIdQuery,
  useDeleteAppVersionMutation,
  useUpdateAppVersionWithoutApkMutation,
  useUpdateAppVersionWithApkMutation,
} = appsVersionsService;
